// src/components/report/manifestTracking/ManifestTrackingDetailDialog.js
import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Chip, CircularProgress, Dialog, DialogContent, DialogTitle,
    IconButton, LinearProgress, Paper, Stack, Tab, Table, TableBody,
    TableCell, TableHead, TableRow, Tabs, Typography,
} from '@mui/material';
import { Close as CloseIcon, Place as PlaceIcon } from '@mui/icons-material';

import { useManifestTrackingDetail } from '../../../hooks/report/useManifestTrackingReport';
import {
    formatDate, formatDateTime, formatDuration, formatManifestCode, formatNumber,
} from '../../../utils/formatters';
import {
    MANIFEST_STATUS_CONFIG, ROLE_CONFIG, END_REASON_LABELS, PARADA_TIPO_CONFIG,
} from '../../../utils/manifestTrackingReportConfig';

import ManifestTimeline from './ManifestTimeline';
import CheckDetailCard from './CheckDetailCard';

const InfoRow = ({ label, value }) => (
    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ py: 0.4 }}>
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
            {label}:
        </Typography>
        <Typography variant="body2" fontWeight={700}>{value || '—'}</Typography>
    </Stack>
);

const diffMinutes = (actual, planned) => {
    if (!actual || !planned) return null;
    const diff = (new Date(actual) - new Date(planned)) / 60000;
    if (Number.isNaN(diff)) return null;
    return Math.round(diff);
};

const formatDiff = (mins) => {
    if (mins == null) return null;
    if (Math.abs(mins) < 1) return 'a tiempo';
    return mins > 0 ? `${mins} min tarde` : `${Math.abs(mins)} min antes`;
};

const ManifestTrackingDetailDialog = ({ open, onClose, manID }) => {
    const [tab, setTab] = useState(0);
    const { data: rawData, isLoading } = useManifestTrackingDetail(manID, open);

    useEffect(() => {
        if (open) setTab(0);
    }, [manID, open]);

    // Blindaje: si la data cacheada no corresponde al manID actual, se ignora
    const data = useMemo(() => {
        if (!rawData) return null;
        if (!manID) return null;
        return String(rawData.manid) === String(manID) ? rawData : null;
    }, [rawData, manID]);

    const turnos = data?.turnos || [];
    const sesiones = data?.sesiones || [];
    const paradas = data?.paradas || [];

    const stats = useMemo(() => {
        const active = turnos.filter((t) => t.abierto).length;
        const totalSeg = turnos.reduce((acc, t) => acc + (t.duracion_seg || 0), 0);
        return { active, totalSeg };
    }, [turnos]);

    const cfg = data
        ? (MANIFEST_STATUS_CONFIG[data.manest] || { label: data.manest, color: '#ccc', text: '#000' })
        : null;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h6" fontWeight={900}>
                            {data
                                ? formatManifestCode(data.manid)
                                : (manID ? formatManifestCode(manID) : 'Cargando...')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {data?.pronom || ''} {data?.vehmat ? `· ${data.vehmat}` : ''}
                            {data?.rutnom ? ` · Ruta: ${data.rutnom}` : ''}
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {cfg && (
                            <Chip label={cfg.label} sx={{ bgcolor: cfg.color, color: cfg.text, fontWeight: 800 }} />
                        )}
                        <IconButton onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </Stack>
            </DialogTitle>

            <DialogContent dividers>
                {isLoading || !data ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Stack spacing={2}>
                        <Tabs
                            value={tab}
                            onChange={(_, v) => setTab(v)}
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            <Tab label="Resumen" />
                            <Tab label="Checks" />
                            <Tab label={`Ruta (${paradas.length})`} />
                            <Tab label={`Turnos (${turnos.length})`} />
                            <Tab label={`Sesiones (${sesiones.length})`} />
                        </Tabs>

                        {/* ---------- TAB 0: RESUMEN ---------- */}
                        {tab === 0 && (
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, flex: 1 }}>
                                    <Typography variant="subtitle2" fontWeight={900} gutterBottom>
                                        Datos del manifiesto
                                    </Typography>
                                    <InfoRow label="Código" value={formatManifestCode(data.manid)} />
                                    <InfoRow label="Proyecto" value={data.pronom} />
                                    <InfoRow label="Ruta" value={data.rutnom} />
                                    <InfoRow label="Vehículo" value={`${data.vehmat || ''} · ${data.vehdes || ''}`.trim()} />
                                    <InfoRow label="Origen" value={data.manori} />
                                    <InfoRow label="Destino" value={data.mandes} />
                                    <InfoRow label="Fecha salida" value={formatDate(data.manfec)} />
                                    <InfoRow label="Fecha fin" value={formatDate(data.manfecfin)} />
                                    <InfoRow
                                        label="Km recorridos"
                                        value={data.km_recorridos != null ? `${formatNumber(data.km_recorridos)} km` : '—'}
                                    />
                                    <InfoRow
                                        label="Paradas visitadas"
                                        value={`${paradas.filter((p) => p.visitada).length} / ${paradas.length}`}
                                    />
                                </Paper>

                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, flex: 1.2 }}>
                                    <Typography variant="subtitle2" fontWeight={900} gutterBottom>
                                        Línea de tiempo
                                    </Typography>
                                    <Box sx={{ mt: 1.5 }}>
                                        <ManifestTimeline data={data} />
                                    </Box>
                                </Paper>
                            </Stack>
                        )}

                        {/* ---------- TAB 1: CHECKS ---------- */}
                        {tab === 1 && (
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                <Box flex={1}>
                                    <CheckDetailCard
                                        title="Check INICIAL"
                                        color="#2e7d32"
                                        data={{
                                            id: data.check_ini_id,
                                            fecha: data.check_ini_fecha,
                                            km: data.check_ini_km,
                                            combustible: data.check_ini_comb,
                                            foto: data.check_ini_foto,
                                            observacion: data.check_ini_obs,
                                            emp_id: data.check_ini_emp,
                                            nombre: data.check_ini_nombre,
                                        }}
                                    />
                                </Box>
                                <Box flex={1}>
                                    <CheckDetailCard
                                        title="Check FINAL"
                                        color="#1565c0"
                                        data={{
                                            id: data.check_fin_id,
                                            fecha: data.check_fin_fecha,
                                            km: data.check_fin_km,
                                            combustible: data.check_fin_comb,
                                            foto: data.check_fin_foto,
                                            observacion: data.check_fin_obs,
                                            emp_id: data.check_fin_emp,
                                            nombre: data.check_fin_nombre,
                                        }}
                                    />
                                </Box>
                            </Stack>
                        )}

                        {/* ---------- TAB 2: RUTA / PARADAS ---------- */}
                        {tab === 2 && (
                            <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                                <Box sx={{ p: 2 }}>
                                    <Typography variant="subtitle2" fontWeight={900}>
                                        Paradas del manifiesto
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Comparación de hora estimada vs. hora real de visita
                                    </Typography>
                                </Box>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align="center"><strong>#</strong></TableCell>
                                            <TableCell><strong>Tipo</strong></TableCell>
                                            <TableCell><strong>Ubicación</strong></TableCell>
                                            <TableCell><strong>Hora estimada</strong></TableCell>
                                            <TableCell><strong>Hora real</strong></TableCell>
                                            <TableCell><strong>Diferencia</strong></TableCell>
                                            <TableCell align="center"><strong>Visitada</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paradas.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={7} align="center">
                                                    <Typography variant="body2" color="text.secondary" py={3}>
                                                        No hay paradas definidas para este manifiesto.
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : paradas.map((p, idx) => {
                                            const tipoCfg = PARADA_TIPO_CONFIG[p.tipo] || {
                                                label: p.tipo, color: '#999',
                                            };
                                            const diff = diffMinutes(p.fecha_visita, p.hor_estimada);
                                            const diffLabel = formatDiff(diff);
                                            const diffColor =
                                                diff == null ? 'text.secondary'
                                                    : Math.abs(diff) <= 5 ? 'success.main'
                                                        : diff > 0 ? 'error.main'
                                                            : 'info.main';

                                            return (
                                                <TableRow key={p.man_par_id} hover>
                                                    <TableCell align="center">
                                                        <Typography fontWeight={800}>
                                                            {p.orden ?? idx + 1}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            size="small"
                                                            label={tipoCfg.label}
                                                            sx={{
                                                                bgcolor: tipoCfg.color,
                                                                color: '#fff',
                                                                fontWeight: 800,
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                                            <PlaceIcon fontSize="small" color="disabled" />
                                                            <Typography variant="body2" fontWeight={700}>
                                                                {p.nombre || '—'}
                                                            </Typography>
                                                        </Stack>
                                                        {p.descripcion && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                {p.descripcion}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {p.hor_estimada ? formatDateTime(p.hor_estimada) : '—'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {p.fecha_visita ? formatDateTime(p.fecha_visita) : '—'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {diffLabel ? (
                                                            <Typography variant="caption" fontWeight={800} color={diffColor}>
                                                                {diffLabel}
                                                            </Typography>
                                                        ) : '—'}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip
                                                            size="small"
                                                            label={p.visitada ? 'Sí' : 'No'}
                                                            color={p.visitada ? 'success' : 'default'}
                                                            variant={p.visitada ? 'filled' : 'outlined'}
                                                            sx={{ fontWeight: 800 }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </Paper>
                        )}

                        {/* ---------- TAB 3: TURNOS ---------- */}
                        {tab === 3 && (
                            <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                                <Box sx={{ p: 2 }}>
                                    <Typography variant="subtitle2" fontWeight={900}>
                                        Turnos registrados (Conductores y Asistentes)
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {stats.active} activo(s) · Duración acumulada: {formatDuration(stats.totalSeg)}
                                    </Typography>
                                </Box>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><strong>Empleado</strong></TableCell>
                                            <TableCell><strong>Rol</strong></TableCell>
                                            <TableCell><strong>Inicio</strong></TableCell>
                                            <TableCell><strong>Fin</strong></TableCell>
                                            <TableCell align="right"><strong>Duración</strong></TableCell>
                                            <TableCell><strong>Motivo fin</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {turnos.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center">
                                                    <Typography variant="body2" color="text.secondary" py={3}>
                                                        Sin turnos registrados
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : turnos.map((t) => {
                                            const roleCfg = ROLE_CONFIG[t.rol] || { label: t.rol, color: 'default' };
                                            return (
                                                <TableRow key={t.role_history_id} hover>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight={700}>
                                                            {t.empleado_nombre || `#${t.emp_id}`}
                                                        </Typography>
                                                        {t.dni && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                DNI: {t.dni}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            size="small"
                                                            label={roleCfg.label}
                                                            color={roleCfg.color}
                                                            variant="outlined"
                                                            sx={{ fontWeight: 700 }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>{formatDateTime(t.start_at)}</TableCell>
                                                    <TableCell>
                                                        {t.end_at ? formatDateTime(t.end_at) : (
                                                            <Chip size="small" label="ACTIVO" color="success" sx={{ fontWeight: 800 }} />
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography fontWeight={800}>
                                                            {formatDuration(t.duracion_seg)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        {t.end_reason ? (END_REASON_LABELS[t.end_reason] || t.end_reason) : '—'}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </Paper>
                        )}

                        {/* ---------- TAB 4: SESIONES ---------- */}
                        {tab === 4 && (
                            <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                                <Box sx={{ p: 2 }}>
                                    <Typography variant="subtitle2" fontWeight={900}>
                                        Sesiones de tracking
                                    </Typography>
                                </Box>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><strong>Session ID</strong></TableCell>
                                            <TableCell><strong>Emp</strong></TableCell>
                                            <TableCell><strong>Rol</strong></TableCell>
                                            <TableCell><strong>Status</strong></TableCell>
                                            <TableCell><strong>Inicio</strong></TableCell>
                                            <TableCell><strong>Fin</strong></TableCell>
                                            <TableCell><strong>Device / App</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {sesiones.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={7} align="center">
                                                    <Typography variant="body2" color="text.secondary" py={3}>
                                                        Sin sesiones registradas
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : sesiones.map((s) => (
                                            <TableRow key={s.session_id} hover>
                                                <TableCell>
                                                    <Typography variant="caption" fontFamily="monospace">
                                                        {String(s.session_id).slice(0, 8)}…
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>#{s.emp_id}</TableCell>
                                                <TableCell>{ROLE_CONFIG[s.rol]?.label || s.rol}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={s.status}
                                                        color={
                                                            s.status === 'ACTIVE' ? 'success'
                                                                : s.status === 'OFFLINE' ? 'warning'
                                                                    : 'default'
                                                        }
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>{formatDateTime(s.started_at)}</TableCell>
                                                <TableCell>{s.ended_at ? formatDateTime(s.ended_at) : '—'}</TableCell>
                                                <TableCell>
                                                    <Typography variant="caption" display="block">
                                                        {s.device_id || '—'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        v{s.app_version || '—'}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Paper>
                        )}
                    </Stack>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default ManifestTrackingDetailDialog;